/**
 * Фронт-сервис словарей Avito.
 *
 * Дёргает наш бэк (relevanter-back/routes/avito.js → avitoDictionariesService),
 * который под капотом ходит либо в Avito Job API (getDicts/getDictByID),
 * либо в публичные XML на CDN Avito (Locations.xml, catalog_nationality.xml).
 *
 * На фронте кэша нет — каждый запрос идёт на бэк. Бэк сам кэширует 24ч
 * глобально (без companyId в ключе), поэтому повторные запросы — мгновенные.
 *
 * Все методы возвращают тип совместимый с SpecializationOption (id/name),
 * чтобы можно было переиспользовать общие UI-компоненты (SpecializationModal
 * и т.п.) без преобразований.
 */
import api from '../relevanter/services/api';
import type { SpecializationOption, AreaOption } from './hhDictionariesService';

/** Элемент словаря Avito (без иерархии). */
export interface AvitoDictItem {
  id: number | string;
  name: string;
  /** Avito помечает устаревшие значения — их нельзя использовать в новых вакансиях. */
  deprecated?: boolean;
}

export interface AvitoLocationHhMappingItem {
  avitoLocationId: string;
  avitoLocationName: string | null;
  avitoLocationType: 'region' | 'city' | null;
  hhAreaId: string | null;
  hhAreaName: string | null;
  status: 'mapped' | 'not_found' | 'ambiguous';
  matchedBy?: string;
  candidates?: Array<{ id: string; name: string }>;
}

export interface AvitoLocationHhMappingResult {
  mappedAreaIds: string[];
  items: AvitoLocationHhMappingItem[];
  unmapped: AvitoLocationHhMappingItem[];
}

/**
 * Специализации Avito для поиска резюме (`specialization`) — плоский CSV-справочник.
 * Возвращается в формате SpecializationOption (`id: string`), чтобы
 * SpecializationModal мог работать с этими данными как и с hh.
 */
export const fetchAvitoResumeSpecializations = async (): Promise<SpecializationOption[]> => {
  const response = await api.get<{ items: AvitoDictItem[] }>('/avito/dictionaries/resume_specializations');
  return (response.data?.items || [])
    .filter((item) => !item.deprecated)
    .map((item) => ({
      id: String(item.id),
      name: item.name,
    }));
};

let avitoResumeSpecializationsCache: SpecializationOption[] | null = null;
let avitoResumeSpecializationsPromise: Promise<SpecializationOption[]> | null = null;

export const getAvitoResumeSpecializationNameById = async (id: string): Promise<string> => {
  const normalizedId = String(id).trim();
  if (!normalizedId) return normalizedId;

  if (!avitoResumeSpecializationsCache) {
    avitoResumeSpecializationsPromise = avitoResumeSpecializationsPromise || fetchAvitoResumeSpecializations();
    try {
      avitoResumeSpecializationsCache = await avitoResumeSpecializationsPromise;
    } finally {
      avitoResumeSpecializationsPromise = null;
    }
  }

  return avitoResumeSpecializationsCache.find((item) => String(item.id) === normalizedId)?.name || normalizedId;
};

/** Настоящий `business_area` вакансий Avito из /job/v2/vacancy/dict/business_area. */
export const fetchAvitoBusinessArea = async (): Promise<SpecializationOption[]> => {
  const response = await api.get<{ items: AvitoDictItem[] }>('/avito/dictionaries/business_area');
  return (response.data?.items || [])
    .filter((item) => !item.deprecated)
    .map((item) => ({
      id: String(item.id),
      name: item.name,
    }));
};

/**
 * Локации Avito (регионы → города → метро/районы).
 * Возвращается в формате AreaOption ({id, name, areas?}), совместимом с
 * существующим AreaModal — это позволяет переиспользовать UI без if-ов.
 *
 * countryId не проставляем: Avito Locations.xml уже содержит **только**
 * российские регионы (одного уровня страны нет). AreaModal в этом случае
 * покажет всё дерево без табов стран.
 */
export const fetchAvitoLocations = async (): Promise<AreaOption[]> => {
  const response = await api.get<{ items: Array<{
    id: string | number;
    name: string;
    cities?: Array<{ id: string | number; name: string }>;
  }> }>('/avito/dictionaries/locations');
  return (response.data?.items || []).map((region) => ({
    id: String(region.id),
    name: region.name,
    areas: (region.cities || []).map((city) => ({
      id: String(city.id),
      name: city.name,
    })),
  }));
};

let avitoLocationsCache: AreaOption[] | null = null;
let avitoLocationsPromise: Promise<AreaOption[]> | null = null;

const getAvitoLocationsCached = async (): Promise<AreaOption[]> => {
  if (!avitoLocationsCache) {
    avitoLocationsPromise = avitoLocationsPromise || fetchAvitoLocations();
    try {
      avitoLocationsCache = await avitoLocationsPromise;
    } finally {
      avitoLocationsPromise = null;
    }
  }
  return avitoLocationsCache;
};

export const compressAvitoLocationIds = async (locationIds: string[]): Promise<string[]> => {
  const selectedIds = Array.from(new Set(
    (Array.isArray(locationIds) ? locationIds : [])
      .map((id) => String(id ?? '').trim())
      .filter(Boolean)
  ));
  if (selectedIds.length === 0) return [];

  const locations = await getAvitoLocationsCached();
  const selectedSet = new Set(selectedIds);
  const knownIds = new Set<string>();

  const buildKnownIndex = (items: AreaOption[]) => {
    items.forEach((item) => {
      knownIds.add(String(item.id));
      if (Array.isArray(item.areas) && item.areas.length > 0) {
        buildKnownIndex(item.areas);
      }
    });
  };
  buildKnownIndex(locations);

  const isFullySelected = (item: AreaOption): boolean => {
    const id = String(item.id);
    if (selectedSet.has(id)) return true;
    const children = Array.isArray(item.areas) ? item.areas : [];
    return children.length > 0 && children.every(isFullySelected);
  };

  const compressNode = (item: AreaOption): string[] => {
    const id = String(item.id);
    if (selectedSet.has(id)) return [id];

    const children = Array.isArray(item.areas) ? item.areas : [];
    if (children.length === 0) return [];
    if (children.every(isFullySelected)) return [id];

    return children.flatMap(compressNode);
  };

  const compressedKnown = locations.flatMap(compressNode);
  const unknownSelected = selectedIds.filter((id) => !knownIds.has(id));

  return Array.from(new Set([...compressedKnown, ...unknownSelected]));
};

export const getAvitoLocationNameById = async (id: string): Promise<string> => {
  const normalizedId = String(id).trim();
  if (!normalizedId) return normalizedId;

  const locations = await getAvitoLocationsCached();
  const stack = [...locations];
  while (stack.length > 0) {
    const item = stack.shift();
    if (!item) continue;
    if (String(item.id) === normalizedId) {
      return item.name || normalizedId;
    }
    if (Array.isArray(item.areas)) {
      stack.push(...item.areas);
    }
  }
  return normalizedId;
};

export const resolveAvitoLocationsToHH = async (locationIds: string[]): Promise<AvitoLocationHhMappingResult> => {
  const response = await api.post<AvitoLocationHhMappingResult>('/avito/dictionaries/locations/hh-mapping', {
    locationIds: locationIds.map(String),
  });
  return response.data;
};

/** Плоский список гражданств Avito (XML catalog_nationality.xml на бэке). */
export const fetchAvitoNationalities = async (): Promise<SpecializationOption[]> => {
  const response = await api.get<{ items: AvitoDictItem[] }>('/avito/dictionaries/nationalities');
  return (response.data?.items || []).map((item) => ({
    id: String(item.id),
    name: item.name,
  }));
};
